<template>
    <div class="dw-editor__document">
        <pdf-viewer
            v-if="!hasErrorOccured"
            :file="pdfSource"
            @error="handleError"
            @fully-loaded="handleLoaded"
        />
        <div v-if="!loaded">
            <p>{{ $t('COMMON.LOADING') }}...</p>
        </div>
        <div v-if="hasErrorOccured">
            <p>
                {{ $t('DOCUMENT_MANAGEMENT_SYSTEM.UNABLE_TO_OPEN_DOCUMENT') }}
            </p>
            <app-button :disabled="isCompanyExpired" @click="handleDownload">
                {{ $t('COMMON.DOWNLOAD') }}
            </app-button>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { fileDownload } from '@/services/utils/functions'
import { permissionControl } from '@/mixins'
import { authService } from '@/services/auth'

export default {
    mixins: [permissionControl],
    props: {
        url: {
            type: String,
            required: false,
            default: ''
        },

        fileName: {
            type: String,
            required: false,
            default: ''
        }
    },

    data () {
        return {
            hasErrorOccured: false,
            loaded: false
        }
    },

    computed: {
        /**
         * The file URL as the API needs it: a guest (no login) proves access with the token in the page URL,
         * so it is passed on to the file request (CT-624). `token` comes from a shared-document link,
         * `access_token` from a task e-mail link.
         */
        fileUrl () {
            const query = this.$route?.query ?? {}
            const params = ['token', 'access_token']
                .filter(name => typeof query[name] === 'string' && query[name])
                .map(name => `${name}=${encodeURIComponent(query[name])}`)

            if (!this.url || !params.length) {
                return this.url
            }

            const separator = this.url.includes('?') ? '&' : '?'

            return `${this.url}${separator}${params.join('&')}`
        },

        /**
         * pdf.js loads the file itself, outside axios, so a logged-in user's Authorization header has to be
         * handed to it explicitly (CT-624).
         */
        pdfSource () {
            const authorization = authService.isLoggedIn()
                ? axios.defaults.headers.Authorization
                : null

            if (!this.fileUrl || !authorization) {
                return this.fileUrl
            }

            return {
                url: this.fileUrl,
                httpHeaders: { Authorization: authorization }
            }
        }
    },

    methods: {
        handleError (error) {
            this.hasErrorOccured = true
            this.loaded = true
            this.$emit('error', error)
        },

        handleLoaded () {
            this.loaded = true
            this.$emit('fully-loaded')
        },

        async handleDownload () {
            this.loaded = false
            try {
                await fileDownload(this.fileUrl, this.fileName)
            } finally {
                this.loaded = true
            }
        }
    }
}
</script>
